/**
 * AI客户分析页面
 *
 * 展示已通过大模型评分的有价值客户列表，用于销售团队快速筛选高价值客户。
 * 数据来源：leads 表 score > 0 的客户 + lead_contact_text 表的画像数据(stdRemark)。
 * 排除已签约(SIGNED_CONTRACT)和已回款(PAID)状态的客户。
 *
 * 核心功能：
 *   - 2个Tab页：AI客户分析、无价值客户
 *   - 客户价值度标记：高/中/低/无，支持快速标记
 *   - 统计卡片区：展示客户总数、平均评分、高分/低分客户数
 *   - 多维度筛选：评分快捷按钮 + 滑块、城市、客户ID、客户姓名、星级、状态、客户类型、入库时间
 *   - 评分排名表格：前3名金银铜徽章、圆形评分徽章
 *   - 评分详情弹窗：展示画像JSON数据（维度、标签、洞察、话术、跟进记录）
 *   - 批量分配：勾选客户后批量分配给销售人员
 *   - AI重新评分：调用 LLM 重新生成客户画像
 *   - 跳转客户详情：点击客户姓名跳转到客户详情页
 *   - 跳转客户画像：点击画像按钮跳转到客户画像页
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table, Button, Form, Modal, Slider, Tag, Space, Toast, Select, Typography, Divider, Tooltip, Tabs, TabPane, Input, DatePicker,
} from "@douyinfe/semi-ui";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";
import { IconStar, IconModalStroked, IconHelpCircle } from "@douyinfe/semi-icons";
import { ApiClient } from "../../api/ApiClient";
import { formatAmountZhCn, formatDateTime, displayStar } from "../../utils";
import PubSub from "pubsub-js";
import dayjs from "dayjs";

const { Text } = Typography;
const apiClient = ApiClient.getDefault();

/* ==================== 类型定义 ==================== */

interface AiAnalysisRecord {
  id: number;
  name: string;
  fullMobile: string;
  applyCity: string;
  applyAmount: number;
  score: number;
  star: number;
  status: string;
  remark: string;
  house: string;
  vehicle: string;
  income: string;
  job: string;
  assignCount: number;
  source: string;
  stdRemark: string;
  stdRemarkAt: string;
  contactLogs: string[];
  contactIntentSummary: any;
  customerValue: string;
  loanType: string;
  createdAt: string;
}

interface PoolStats {
  totalCount: number;
  avgScore: number;
  highScoreCount: number;
  mediumScoreCount: number;
  lowScoreCount: number;
}

interface ProfileDimension {
  name: string;
  score: number;
  reason: string;
}

interface CustomerProfile {
  customer_name: string;
  total_score: number;
  star: number;
  ai_conclusion: string;
  dimensions: ProfileDimension[];
  profile_mapping: Record<string, string>;
  tags: string[];
  insight: string;
  follow_up_script: string;
}

/* ==================== 配置常量 ==================== */

const CUSTOMER_VALUE_CONFIG: Record<string, { label: string; color: string }> = {
  HIGH: { label: "高", color: "green" },
  MEDIUM: { label: "中", color: "blue" },
  LOW: { label: "低", color: "orange" },
  NONE: { label: "无", color: "grey" },
};

const CUSTOMER_VALUE_OPTIONS = [
  { value: "", label: "全部" },
  { value: "HIGH", label: "高" },
  { value: "MEDIUM", label: "中" },
  { value: "LOW", label: "低" },
  { value: "NONE", label: "无" },
];

const STATUS_OPTIONS = [
  { value: "", label: "全部状态" },
  { value: "PENDING_CONTACT", label: "未处理" },
  { value: "UNREACHABLE", label: "待跟进" },
  { value: "OUT_LEAD", label: "外地客户" },
  { value: "NON_COMPLIANCE", label: "资质不符" },
  { value: "INVITATION_IN_PROGRESS", label: "邀约中" },
  { value: "PENDING_VISIT", label: "待上门" },
  { value: "VISITED", label: "已上门" },
  { value: "SIGNED_CONTRACT", label: "已签约" },
  { value: "PAID", label: "已回款" },
];

const LOAN_TYPE_OPTIONS = [
  { value: "", label: "全部类型" },
  { value: "MORTGAGE", label: "信贷" },
  { value: "CARD", label: "信用卡" },
  { value: "BORROW", label: "借款" },
];

const STAR_OPTIONS = [
  { value: 0, label: "全部星级" },
  { value: 1, label: "1星" },
  { value: 2, label: "2星" },
  { value: 3, label: "3星" },
  { value: 4, label: "4星" },
  { value: 5, label: "5星" },
];

/* ==================== 工具函数 ==================== */

const getScoreColor = (score: number): string => {
  if (score >= 60) return "var(--semi-color-success)";
  if (score >= 40) return "var(--semi-color-warning)";
  return "var(--semi-color-danger)";
};

const getScoreHexColor = (score: number): string => {
  if (score >= 60) return "#28a745";
  if (score >= 40) return "#faad14";
  return "#ff4d4f";
};

/** 排名徽章：前3名金银铜，其余普通数字 */
const RankBadge: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank <= 3) {
    const cfg = [
      { bg: "#fff7e6", border: "#ffd700", text: "#b8860b" },
      { bg: "#f5f5f5", border: "#c0c0c0", text: "#666" },
      { bg: "#fff2e8", border: "#cd7f32", text: "#8b4513" },
    ][rank - 1];
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: "50%",
        background: cfg.bg, border: `2px solid ${cfg.border}`,
        color: cfg.text, fontWeight: 700, fontSize: 12,
      }}>{rank}</span>
    );
  }
  return <Text type="tertiary" style={{ fontSize: 13 }}>{rank}</Text>;
};

/** 圆形评分徽章 */
const ScoreBadge: React.FC<{ score: number }> = ({ score }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 40, height: 40, borderRadius: "50%",
    background: `${getScoreHexColor(score)}15`,
    border: `2px solid ${getScoreHexColor(score)}`,
    color: getScoreHexColor(score), fontWeight: 700, fontSize: 14,
  }}>{score}</span>
);

/** 安全解析 JSON */
const safeParseJson = (raw: any): any => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

/* ==================== 统计卡片样式 ==================== */

const statCardStyle = (accentColor: string): React.CSSProperties => ({
  flex: 1, minWidth: 150, padding: "16px 20px",
  background: "var(--semi-color-bg-0)", borderRadius: 8,
  border: "1px solid var(--semi-color-border)", borderLeft: `3px solid ${accentColor}`,
});

/* ==================== 主组件 ==================== */

export default function AiCustomerAnalysis() {
  // Tab状态
  const [activeTab, setActiveTab] = useState("analysis");

  // 表格数据
  const [data, setData] = useState<AiAnalysisRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // 筛选条件
  const [minScore, setMinScore] = useState(1);
  const [city, setCity] = useState("");
  const [leadId, setLeadId] = useState("");
  const [leadName, setLeadName] = useState("");
  const [starFilter, setStarFilter] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [loanTypeFilter, setLoanTypeFilter] = useState("");
  const [customerValueFilter, setCustomerValueFilter] = useState("");
  const [createdAtRange, setCreatedAtRange] = useState<any[]>([]);

  // 选择与操作
  const [selectedRows, setSelectedRows] = useState<AiAnalysisRecord[]>([]);
  const [llmScoring, setLlmScoring] = useState(false);

  // 弹窗状态
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState<AiAnalysisRecord | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignFormApi, setAssignFormApi] = useState<FormApi>();
  const [assignSaving, setAssignSaving] = useState(false);
  const [teamMembersTree, setTeamMembersTree] = useState<any[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
  const selectedMembersRef = useRef<any[]>([]);

  // 统计数据
  const [stats, setStats] = useState<PoolStats | null>(null);

  // 城市选项
  const [cityOptions, setCityOptions] = useState<any[]>([]);

  const role = localStorage.getItem("role") || "";

  /* ---------- 数据加载 ---------- */

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page, size: pageSize,
        excludeStatuses: "SIGNED_CONTRACT,PAID",
      };

      // 根据Tab设置不同的查询参数
      if (activeTab === "analysis") {
        params.minScore = Math.max(minScore, 1);
      } else {
        // 无价值客户Tab: customerValue = NONE 或 null
        params.customerValue = "NONE";
      }

      if (city) params.city = city;
      if (leadId) params.leadId = leadId;
      if (leadName) params.leadName = leadName;
      if (starFilter > 0) params.star = starFilter;
      if (statusFilter) params.status = statusFilter;
      if (loanTypeFilter) params.loanType = loanTypeFilter;
      if (customerValueFilter && activeTab === "analysis") params.customerValue = customerValueFilter;
      if (createdAtRange && createdAtRange.length === 2) {
        params.createdAtStart = dayjs(createdAtRange[0]).format("YYYY-MM-DD");
        params.createdAtEnd = dayjs(createdAtRange[1]).format("YYYY-MM-DD");
      }

      const resp = await apiClient.request<{ current: number; total: number; records: AiAnalysisRecord[] }>(
        "/qualification/aiAnalysisPool", "GET", params
      );
      if (resp.data) {
        setData(resp.data.records || []);
        setTotal(resp.data.total || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, pageSize, minScore, city, leadId, leadName, starFilter, statusFilter, loanTypeFilter, customerValueFilter, createdAtRange, activeTab]);

  const loadStats = useCallback(async () => {
    try {
      const params: Record<string, any> = {
        excludeStatuses: "SIGNED_CONTRACT,PAID",
      };
      if (activeTab === "analysis") {
        params.minScore = Math.max(minScore, 1);
      } else {
        params.customerValue = "NONE";
      }
      if (city) params.city = city;
      const resp = await apiClient.request<PoolStats>("/qualification/aiAnalysisPool/stats", "GET", params);
      if (resp.data) setStats(resp.data);
    } catch (e) { console.error(e); }
  }, [minScore, city, activeTab]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const username = localStorage.getItem("username") || "";
    const effectiveUsername = (role === "op_manager" || role === "root" || role === "monitor") ? "root" : username;
    apiClient.getTeamMemberTreeAsOptsOfAny(effectiveUsername).then((opts) => setTeamMembersTree(opts));
    apiClient.getDictAsOpts("city").then((opts) => setCityOptions(opts));
  }, []);

  useEffect(() => {
    selectedMembersRef.current = selectedMembers;
  }, [selectedMembers]);

  /* ---------- 事件处理 ---------- */

  const handleCreatedAtChange = date => {
      setCreatedAtRange(date);
  };

  /** 更新客户价值度 */
  const handleUpdateCustomerValue = async (leadId: number, value: string) => {
    try {
      await apiClient.request("/leads/updateCustomerValue", "POST", { leadId, value });
      Toast.success("标记成功");
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "标记失败");
    }
  };

  /** 批量更新客户价值度 */
  const handleBatchUpdateCustomerValue = async (value: string) => {
    if (selectedRows.length === 0) { Toast.warning("请先选择客户"); return; }
    try {
      const leadIds = selectedRows.map((r) => r.id);
      await apiClient.request("/leads/batchUpdateCustomerValue", "POST", { leadIds, value });
      Toast.success("批量标记成功");
      setSelectedRows([]);
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "批量标记失败");
    }
  };

  /** 查看详情 */
  const handleViewDetail = (record: AiAnalysisRecord) => {
    setDetailData(record);
    setShowDetailModal(true);
  };

  /** AI重新评分 */
  const handleRescore = async (record: AiAnalysisRecord) => {
    Modal.confirm({
      title: record.score > 0 ? '重新 AI 评分' : 'AI 客户画像评分',
      content: record.score > 0
        ? `该客户已有 AI 评分（${record.score} 分），重新评分将覆盖现有数据。是否继续？`
        : '将通过大模型分析客户资质并生成画像评分，是否继续？',
      onOk: async () => {
        setLlmScoring(true);
        try {
          await apiClient.request('/qualification/llmScore', 'POST', {
            leadId: record.id,
            mode: 'dict',
          });
          Toast.success('AI评分完成');
          loadData();
          loadStats();
        } catch (e: any) {
          Toast.error(e?.message || 'AI评分失败');
        } finally {
          setLlmScoring(false);
        }
      }
    });
  };

  /** 批量AI重新评分 */
  const handleBatchRescore = async () => {
    if (selectedRows.length === 0) { Toast.warning("请先选择客户"); return; }
    Modal.confirm({
      title: '批量 AI 重新评分',
      content: `将对 ${selectedRows.length} 位客户进行 AI 重新评分，是否继续？`,
      onOk: async () => {
        setLlmScoring(true);
        let success = 0;
        let fail = 0;
        for (const row of selectedRows) {
          try {
            await apiClient.request('/qualification/llmScore', 'POST', {
              leadId: row.id,
              mode: 'dict',
            });
            success++;
          } catch {
            fail++;
          }
        }
        setLlmScoring(false);
        Toast.success(`评分完成：成功 ${success}，失败 ${fail}`);
        setSelectedRows([]);
        loadData();
        loadStats();
      }
    });
  };

  const handleAssignClick = () => {
    if (selectedRows.length === 0) { Toast.warning("请先选择客户"); return; }
    setShowAssignModal(true);
    const strIds = localStorage.getItem("assignToIds");
    if (strIds) {
      apiClient.request("/users/removeLeaveUserId", "GET", { ids: JSON.parse(strIds) })
        .then((resp: any) => setSelectedMembers(resp.data));
    }
  };

  const doAssign = async () => {
    setAssignSaving(true);
    try {
      await assignFormApi!.validate();
      const formValues = assignFormApi!.getValues();
      await apiClient.request("/leadScoring/assign", "POST", undefined, {
        leadIds: selectedRows.map((r) => r.id),
        assignToIds: formValues.assignToIds,
        assignLeadType: formValues.assignLeadType || "my",
      });
      localStorage.setItem("assignToIds", JSON.stringify(formValues.assignToIds));
      localStorage.setItem("my-refresh", "true");
      localStorage.setItem("new-refresh", "true");
      localStorage.setItem("all-refresh", "true");
      Toast.success("分配成功");
      setShowAssignModal(false);
      setSelectedRows([]);
      loadData();
      loadStats();
      PubSub.publish("refreshMenuCount", "");
    } catch (e: any) { Toast.error(e?.message || "分配失败"); }
    finally { setAssignSaving(false); }
  };

  /* ---------- 跳转功能 ---------- */

  /** 跳转客户详情 */
  const handleViewLeadDetail = (record: AiAnalysisRecord) => {
    const w = window.open("about:blank");
    if (w != null) {
      const data = { id: record.id, ids: [] };
      w.location.href = "/#/leadDetailNew?data=" + encodeURIComponent(JSON.stringify(data));
    }
  };

  /** 跳转客户画像 */
  const handleViewLeadProfile = (record: AiAnalysisRecord) => {
    const w = window.open("about:blank");
    if (w != null) {
      w.location.href = `/#/leadProfile?id=${record.id}`;
    }
  };

  /* ---------- 表格列定义 ---------- */

  const columns = [
    {
      title: "排名", dataIndex: "index", width: 60,
      render: (_: any, __: any, index: number) => <RankBadge rank={(page - 1) * pageSize + index + 1} />,
    },
    {
      title: "客户ID", dataIndex: "id", width: 80,
    },
    {
      title: "客户", dataIndex: "name", width: 100,
      render: (value: any, record: AiAnalysisRecord) => (
        <Typography.Text link onClick={() => handleViewLeadDetail(record)}>
          {value}
        </Typography.Text>
      ),
    },
    {
      title: "AI评分", dataIndex: "score", width: 80,
      render: (score: number) => <ScoreBadge score={score} />,
    },
    {
      title: "星级", dataIndex: "star", width: 55,
      render: (v: number) => displayStar(v),
    },
    {
      title: "意向",
      dataIndex: "intentionLevel",
      width: 70,
      render: (lvl?: string) => {
        if (!lvl) return <span style={{ color: "#9ca3af" }}>-</span>;
        const color = lvl === "A" ? "#22c55e" : lvl === "B" ? "#facc15" :
                      lvl === "C" ? "#f97316" : lvl === "D" ? "#ef4444" : "#9ca3af";
        return (
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 24, height: 24, borderRadius: "50%",
            background: color, color: "#fff", fontSize: 12, fontWeight: 700,
          }}>{lvl}</span>
        );
      },
    },
    {
      title: "客户价值度", dataIndex: "customerValue", width: 120,
      render: (value: string, record: AiAnalysisRecord) => {
        const cfg = CUSTOMER_VALUE_CONFIG[value] || CUSTOMER_VALUE_CONFIG.NONE;
        return (
          <Select
            value={value || "NONE"}
            style={{ width: 80 }}
            size="small"
            onChange={(v: string) => handleUpdateCustomerValue(record.id, v)}
            optionList={[
              { value: "HIGH", label: "高" },
              { value: "MEDIUM", label: "中" },
              { value: "LOW", label: "低" },
              { value: "NONE", label: "无" },
            ]}
          />
        );
      },
    },
    {
      title: "资质", width: 200,
      render: (_: any, r: AiAnalysisRecord) => (
        <Space style={{ gap: 4 }} wrap>
          {r.house && r.house !== "无房" && <Tag size="small" color="blue">{r.house}</Tag>}
          {r.vehicle && r.vehicle !== "无车" && <Tag size="small" color="cyan">{r.vehicle}</Tag>}
          {r.income && <Tag size="small">{r.income}</Tag>}
          {r.job && <Tag size="small" color="purple">{r.job}</Tag>}
        </Space>
      ),
    },
    {
      title: "申请金额", dataIndex: "applyAmount", width: 90,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmountZhCn(v)}</span>,
    },
    {
      title: "城市", dataIndex: "applyCity", width: 70,
    },
    {
      title: "状态", dataIndex: "status", width: 80,
      render: (v: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          PENDING_CONTACT: { label: "未处理", color: "grey" },
          UNREACHABLE: { label: "待跟进", color: "orange" },
          OUT_LEAD: { label: "外地", color: "cyan" },
          NON_COMPLIANCE: { label: "资质不符", color: "red" },
          INVITATION_IN_PROGRESS: { label: "邀约中", color: "blue" },
          PENDING_VISIT: { label: "待上门", color: "purple" },
          VISITED: { label: "已上门", color: "green" },
        };
        const cfg:any = statusMap[v];
        return cfg ? <Tag size="small" color={cfg.color}>{cfg.label}</Tag> : <Text type="tertiary">-</Text>;
      },
    },
    {
      title: "画像结论", dataIndex: "stdRemark", width: 200,
      render: (value: string) => {
        const profile = safeParseJson(value);
        if (!profile) return <Text type="tertiary">未生成画像</Text>;
        return (
          <Tooltip content={profile.ai_conclusion} style={{ maxWidth: 300 }}>
            <Text ellipsis={{ showTooltip: false }} style={{ maxWidth: 200 }}>
              {profile.ai_conclusion || '-'}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "入库时间", dataIndex: "createdAt", width: 120,
      render: (v: string) => v ? formatDateTime(v) : "-",
    },
    {
      title: "操作", width: 120, fixed: "right" as const,
      render: (_: any, record: AiAnalysisRecord) => (
        <>
          <Button theme="borderless" size="small" onClick={() => handleViewDetail(record)}>详情</Button>
          <Button theme="borderless" size="small" onClick={() => handleViewLeadProfile(record)}>画像</Button>
          <Button theme="borderless" size="small" type="warning" onClick={() => handleRescore(record)} loading={llmScoring}>
            AI重新评分
          </Button>
        </>
      ),
    },
  ];

  /* ==================== JSX ==================== */

  const renderAssignLabel = (label: any, item: any) => (
    <div style={{ padding: "6px 8px", lineHeight: 1.0 }}>
      <div style={{ fontWeight: 500, color: "var(--semi-color-text-0)", fontSize: 14 }}>
        {label}
        <span style={{ fontSize: 12, color: "var(--semi-color-text-2)", marginLeft: 10 }}>
          {item.labelSubText}
        </span>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 8, background: "var(--semi-color-bg-1)", minHeight: "100%" }}>

      {/* ====== Tab切换 ====== */}
      <Tabs type="line" activeKey={activeTab} onChange={(key) => { setActiveTab(key as string); setPage(1); setSelectedRows([]); }}>
        <TabPane tab="AI客户分析" itemKey="analysis" />
        <TabPane tab="无价值客户" itemKey="valueless" />
      </Tabs>

      {/* ====== 统计卡片区 ====== */}
      <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap", }}>
        <div style={statCardStyle("var(--semi-color-primary)")}>
          <Text type="tertiary" size="small">{activeTab === "analysis" ? "有价值客户总数" : "无价值客户总数"}</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-text-0)", marginTop: 4 }}>
            {stats?.totalCount ?? "-"}
          </div>
        </div>
        <div style={statCardStyle(getScoreColor(stats?.avgScore ?? 50))}>
          <Text type="tertiary" size="small">平均评分</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: getScoreColor(stats?.avgScore ?? 50), marginTop: 4 }}>
            {stats?.avgScore != null ? stats.avgScore.toFixed(1) : "-"}
          </div>
        </div>
        <div style={statCardStyle("var(--semi-color-success)")}>
          <Text type="tertiary" size="small">高分客户 (≥60)</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-success)", marginTop: 4 }}>
            {stats?.highScoreCount ?? "-"}
          </div>
        </div>
        <div style={statCardStyle("var(--semi-color-warning)")}>
          <Text type="tertiary" size="small">中等客户 (40-59)</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-warning)", marginTop: 4 }}>
            {stats?.mediumScoreCount ?? "-"}
          </div>
        </div>
        <div style={statCardStyle("var(--semi-color-danger)")}>
          <Text type="tertiary" size="small">低分客户 (&lt;40)</Text>
          <div style={{ fontSize: 28, fontWeight: 700, color: "var(--semi-color-danger)", marginTop: 4 }}>
            {stats?.lowScoreCount ?? "-"}
          </div>
        </div>
      </div>

      {/* ====== 筛选栏 ====== */}
      <div style={{
        background: "var(--semi-color-bg-0)", borderRadius: 8, padding: "8px 8px",
        marginBottom: 8, border: "1px solid var(--semi-color-border)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {activeTab === "analysis" && (
            <>
              <Space>
                <Button size="small" type={minScore === 1 ? "primary" : "tertiary"}
                  onClick={() => { setMinScore(1); setPage(1); }}>全部</Button>
                <Button size="small" type={minScore === 60 ? "primary" : "tertiary"}
                  onClick={() => { setMinScore(60); setPage(1); }}
                  style={{ color: minScore === 60 ? "#fff" : "#28a745" }}>高分(60+)</Button>
                <Button size="small" type={minScore === 40 ? "primary" : "tertiary"}
                  onClick={() => { setMinScore(40); setPage(1); }}>中等(40+)</Button>
              </Space>
              <Divider layout="vertical" />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Text size="small" type="tertiary">最低分:</Text>
                <Slider value={minScore} min={1} max={100} step={5} style={{ width: 100 }}
                  onChange={(v) => setMinScore(v as number)} onAfterChange={() => setPage(1)} />
                <Text size="small" strong>{minScore}</Text>
              </div>
              <Divider layout="vertical" />
            </>
          )}
          <Input placeholder="客户ID" value={leadId} onChange={setLeadId} style={{ width: 100 }}
            onEnterPress={() => { setPage(1); }} />
          <Input placeholder="客户姓名" value={leadName} onChange={setLeadName} style={{ width: 100 }}
            onEnterPress={() => { setPage(1); }} />
          <Select placeholder="星级" value={starFilter} style={{ width: 100 }}
            onChange={(v: number) => { setStarFilter(v); setPage(1); }}
            optionList={STAR_OPTIONS} />
          <Select placeholder="状态" value={statusFilter} style={{ width: 120 }}
            onChange={(v: string) => { setStatusFilter(v || ""); setPage(1); }}
            optionList={STATUS_OPTIONS} />
          <Select placeholder="客户类型" value={loanTypeFilter} style={{ width: 120 }}
            onChange={(v: string) => { setLoanTypeFilter(v || ""); setPage(1); }}
            optionList={LOAN_TYPE_OPTIONS} />
          {activeTab === "analysis" && (
            <Select placeholder="客户价值度" value={customerValueFilter} style={{ width: 120 }}
              onChange={(v: string) => { setCustomerValueFilter(v || ""); setPage(1); }}
              optionList={CUSTOMER_VALUE_OPTIONS} />
          )}
          <Select placeholder="城市" value={city} style={{ width: 120 }}
            onChange={(v: string) => { setCity(v || ""); setPage(1); }}
            optionList={[{ value: "", label: "全部城市" }, ...cityOptions]} />
          <DatePicker type="dateRange" placeholder={["入库开始时间", "入库结束时间"]}
            value={createdAtRange} onChange={handleCreatedAtChange} style={{ width: 240 }} />
          <Button theme="solid" onClick={() => { setPage(1); loadData(); loadStats(); }}>查询</Button>
        </div>
      </div>

      {/* ====== 操作栏 ====== */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 12, padding: "0 4px",
      }}>
        <Space>
          <Text type="tertiary" size="small">共 {total} 条</Text>
          {selectedRows.length > 0 && (
            <Tag color="blue" size="small">已选 {selectedRows.length} 项</Tag>
          )}
        </Space>
        <Space>
          {activeTab === "analysis" && (
            <>
              <Button size="small" type="warning" icon={<IconStar />}
                loading={llmScoring} disabled={selectedRows.length === 0}
                onClick={handleBatchRescore}>AI重新评分({selectedRows.length})</Button>
              <Select placeholder="批量标记价值度" size="small" style={{ width: 120 }}
                onChange={(v: string) => { if (v) handleBatchUpdateCustomerValue(v); }}
                optionList={[
                  { value: "HIGH", label: "高价值" },
                  { value: "MEDIUM", label: "中价值" },
                  { value: "LOW", label: "低价值" },
                  { value: "NONE", label: "无价值" },
                ]} />
            </>
          )}
          <Button theme="solid" disabled={selectedRows.length === 0}
            onClick={handleAssignClick}>分配({selectedRows.length})</Button>
        </Space>
      </div>

      {/* ====== 数据表格 ====== */}
      <Table columns={columns} dataSource={data} loading={loading} rowKey="id"
        scroll={{ x: 1400 }}
        pagination={{ currentPage: page, pageSize, total, onPageChange: (p) => setPage(p), showTotal: true }}
        rowSelection={{ onChange: (_, rows) => setSelectedRows(rows as AiAnalysisRecord[]) }}
        size="small" bordered
      />

      {/* ====== 评分详情弹窗 ====== */}
      <Modal title="AI评分详情" visible={showDetailModal} onCancel={() => setShowDetailModal(false)}
        footer={null} width={1500} style={{ top: 20 }}
        bodyStyle={{ maxHeight: "calc(90vh - 120px)", overflow: "auto", padding: 0 }}>
        {detailData && renderProfileDetail(detailData)}
      </Modal>

      {/* ====== 分配弹窗 ====== */}
      <Modal
        icon={<IconModalStroked size="extra-large" style={{ color: "blue" }} />}
        style={{ width: 1600, height: 650 }}
        title="分配客户" visible={showAssignModal} okText="分配"
        confirmLoading={assignSaving} onOk={doAssign} onCancel={() => setShowAssignModal(false)}
      >
        <Form getFormApi={(api) => setAssignFormApi(api)}>
          <Form.TreeSelect
            placeholder="请选择分配给谁"
            rules={[{ required: true, message: "请填写" }]}
            style={{ width: "100%" }}
            field="assignToIds" label="销售"
            checkRelation="unRelated" leafOnly multiple
            treeData={teamMembersTree}
            initValue={selectedMembersRef.current}
            filterTreeNode showFilteredOnly
            searchPlaceholder="请输入关键字开始搜索"
            renderLabel={renderAssignLabel}
            autoAdjustOverflow={false}
            virtualize={{ itemSize: 28, height: 400 }}
          />
          <Form.Select
            rules={[{ required: true, message: "请填写" }]}
            disabled
            initValue={selectedRows.map((r) => r.id)}
            style={{ width: "100%" }}
            multiple field="leadIds" label="客户"
          >
            {selectedRows.map((r) => (
              <Form.Select.Option key={r.id + ""} value={r.id}>{r.name}</Form.Select.Option>
            ))}
          </Form.Select>
          {(role === "root" || role === "op_manager" || role === "monitor") && (
            <Form.Select
              rules={[{ required: true, message: "请填写" }]}
              initValue="my"
              style={{ width: "100%" }}
              field="assignLeadType"
              label={{
                text: "分配客户类型",
                extra: (
                  <Tooltip style={{ maxWidth: 400 }} content={"选择新客户，客户会作为新客户分配"}>
                    <IconHelpCircle style={{ color: "#E91E63" }} />
                  </Tooltip>
                ),
              }}
            >
              <Form.Select.Option key="my" value="my">再分配客户</Form.Select.Option>
              <Form.Select.Option key="new" value="new">新客户</Form.Select.Option>
            </Form.Select>
          )}
        </Form>
      </Modal>
    </div>
  );
}

/* ==================== 画像详情渲染 ==================== */

function renderProfileDetail(d: AiAnalysisRecord) {
  const profile: CustomerProfile | null = safeParseJson(d.stdRemark);

  if (!profile) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Text type="tertiary">该客户尚未生成AI画像</Text>
        <div style={{ marginTop: 16 }}>
          <Text type="tertiary">请在客户详情页点击"AI评分"按钮生成画像</Text>
        </div>
      </div>
    );
  }

  const sectionTitle: React.CSSProperties = {
    fontSize: 13, fontWeight: 600, display: "block", marginBottom: 8,
    color: "var(--semi-color-text-1)", paddingLeft: 8,
    borderLeft: "3px solid var(--semi-color-primary)",
  };

  return (
    <div>
      {/* 顶部信息栏 */}
      <div style={{
        display: "flex", alignItems: "center", gap: 20, padding: "16px 20px",
        background: "var(--semi-color-bg-0)", borderBottom: "1px solid var(--semi-color-border)",
      }}>
        <ScoreBadge score={profile.total_score} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text strong style={{ fontSize: 16 }}>{d.name || "-"}</Text>
            {d.star > 0 && displayStar(d.star)}
            <Tag size="small">{d.applyCity || "-"}</Tag>
            <Tag size="small" color="blue">{formatAmountZhCn(d.applyAmount)}</Tag>
          </div>
          {profile.ai_conclusion && (
            <Text  type="warning" style={{ display: "block", marginTop: 8 }}>
              {profile.ai_conclusion}
            </Text>
          )}
        </div>
         {/* 标签 */}
          {profile.tags && profile.tags.length > 0 && (
            <div style={{ marginBottom: 16, width: 600 }}>
              <span style={sectionTitle}>标签</span>
              <Space wrap spacing={4}>
                {profile.tags.map((tag, i) => <Tag key={i} size="small" color="blue">{tag}</Tag>)}
              </Space>
            </div>
          )}

        
      </div>

       {/* 维度评分 */}
          {profile.dimensions && profile.dimensions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <span style={sectionTitle}>维度评分</span>
              <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
                {profile.dimensions.map((dim, i) => (
                  <div key={i} style={{
                    padding: "8px 12px", background: "var(--semi-color-fill-0)",
                    borderRadius: 6,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <Text size="small" strong>{dim.name}</Text>
                      <Text size="small" style={{ color: getScoreHexColor(dim.score), fontWeight: 700 }}>{dim.score}</Text>
                    </div>
                    <div style={{ width: "100%", height: 4, background: "var(--semi-color-fill-2)", borderRadius: 2 }}>
                      <div style={{ width: `${dim.score}%`, height: "100%", borderRadius: 2, background: getScoreHexColor(dim.score) }} />
                    </div>
                    {dim.reason && (
                      <Text size="small" type="tertiary" style={{ display: "block", marginTop: 4, lineHeight: 1.5 }}>{dim.reason}</Text>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
         {/* 客户档案 */}
          {profile.profile_mapping && Object.keys(profile.profile_mapping).length > 0 && (
            <div>
              <span style={sectionTitle}>客户档案</span>
              <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
                {Object.entries(profile.profile_mapping).map(([key, value]) => (
                  <div key={key} style={{ display: "flex", fontSize: 13, gap: 4 }}>
                    <Text type="tertiary" style={{ whiteSpace: "nowrap" }}>{key}：</Text>
                    <Text>{value}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        
      <Divider></Divider>          

      {/* 两栏内容区 */}
      <div style={{ display: "flex", gap: 0 ,marginTop:16}}>
        {/* 左栏： */}
        <div style={{ flex: "0 0 720px", padding: 0, borderRight: "1px solid var(--semi-color-border)" }}>
            {/* 决策洞察 */}
          {profile.insight && (
            <div style={{ marginBottom: 16 }}>
              <span style={sectionTitle}>决策洞察</span>
              <div style={{ fontSize: 13, lineHeight: 1.8, padding: "10px 12px", background: "var(--semi-color-fill-0)", borderRadius: 6 }}>
                {profile.insight}
              </div>
            </div>
          )}

          {/* 跟进话术 */}
          {profile.follow_up_script && (
            <div style={{ marginBottom: 16 }}>
              <span style={sectionTitle}>跟进话术</span>
              <div style={{
                fontSize: 13, lineHeight: 1.8, whiteSpace: "pre-wrap",
                padding: "10px 12px", background: "var(--semi-color-fill-0)", borderRadius: 6,
              }}>
                {profile.follow_up_script}
              </div>
            </div>
          )}
        </div>


        {/* 右栏：洞察 + 话术 + 备注 + 跟进 */}
        <div style={{ flex: 1, padding: 16, minWidth: 0 }}>
          

          {/* 资质备注 */}
          {d.remark && (
            <div style={{ marginBottom: 16 }}>
              <span style={sectionTitle}>资质备注</span>
              <div style={{
                padding: "10px 12px", background: "var(--semi-color-fill-0)", borderRadius: 6,
                fontSize: 13, maxHeight: 120, overflow: "auto", lineHeight: 1.8,
              }}>
                {d.remark}
              </div>
            </div>
          )}

          {/* 跟进记录 */}
          {d.contactLogs && d.contactLogs.length > 0 && (
            <div>
              <span style={sectionTitle}>
                跟进记录 <Tag size="small" color="grey">{d.contactLogs.length}</Tag>
              </span>
              <div style={{ maxHeight: 260, overflow: "auto" }}>
                {d.contactLogs.slice(0, 10).map((log, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 8, padding: "6px 0",
                    borderBottom: i < Math.min(d.contactLogs.length, 10) - 1 ? "1px solid var(--semi-color-border)" : "none",
                  }}>
                    <div style={{
                      width: 6, height: 6, borderRadius: "50%", marginTop: 6, flexShrink: 0,
                      background: "var(--semi-color-primary)",
                    }} />
                    <div style={{ fontSize: 13, lineHeight: 1.6 }}>{log}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
